import {
  type Interpolation, type SerializedStyles,
} from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type Ref, useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { isArray } from '~/_shared/utils/collections/collections';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { OverlayLottieAnimationComponent } from '../../components/overlay/overlayLottieAnimation.component';
import { OverlayWrapperComponent } from '../../components/overlay/overlayWrapper.component';
import {
  SelectedItemComponent, Size,
} from '../../components/selectedItem/selectedItem.component';
import { usePrevious } from '../../utils/hooks/usePrevious';
import {
  DropdownComponent, dropdownItemsContainerStyle, DropDownItemSize, DropdownPlacement, type DropdownProps,
} from '../dropdown';
import { InputSize } from '../inputs';
import { standardLoaderAnimationSettings } from '../loaders';
import {
  type LottieAnimationProps, useLottieAnimationDefaultColors,
} from '../lottieAnimation';
import {
  autocompleteNoResultsStyle, dropdownContainerStyle, selectedItemStyle, selectedItemTextStyle,
} from './autocomplete.styles';
import {
  type AutocompleteOption, type AutocompleteRegularOption,
} from './autocomplete.types';
import { AutocompleteItem } from './components';
import {
  AutocompleteTrigger, type AutocompleteTriggerProps,
} from './private/components/autocompleteTrigger';
import {
  compareOptionsValues, isCustomOption,
} from './utils';

type BaseAutocompleteComponentProps<T> = Readonly<{
  checkSelected?: boolean;
  className?: string;
  customValueComparer?: (option: T, value: T) => boolean;
  hideChevron?: boolean;
  hideNoResultsMessage?: boolean;
  icon?: IconProp | null;
  iconStyle?: SerializedStyles;
  inputSize?: InputSize;
  inputStyle?: Interpolation;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  itemSize?: DropDownItemSize;
  loaderConfig?: LottieAnimationProps;
  maxSelectedVisible?: number;
  onInputChange?: (value: string) => void;
  onOpenChange?: (isVisible: boolean) => void;
  options: ReadonlyArray<AutocompleteOption<T>>;
  placeholder?: string;
  selectionLimit?: number;
  triggerInputRef?: Ref<HTMLInputElement>;
}> & Pick<DropdownProps,
'inPortal' |
'menuStyle'
> & Pick<AutocompleteTriggerProps, 'onBlur' | 'onKeyUp'>;

export type MultipleAutocompleteProps<T> = {
  value: T[];
  onChange: (value: T[]) => void;
  multiple: true;
} & BaseAutocompleteComponentProps<T>;

export type SingleAutocompleteProps<T> = {
  value: T | null;
  onChange: (value: T | null) => void;
  multiple?: false;
} & BaseAutocompleteComponentProps<T>;

export type AutocompleteComponentProps<T> = MultipleAutocompleteProps<T> | SingleAutocompleteProps<T>;

export const AutocompleteComponent = <T, >({
  checkSelected,
  customValueComparer,
  hideChevron,
  hideNoResultsMessage,
  icon = null,
  iconStyle,
  inPortal,
  inputSize = InputSize.Medium,
  inputStyle,
  isClearable,
  isDisabled,
  isLoading,
  isOpen,
  itemSize = DropDownItemSize.Default,
  loaderConfig,
  maxSelectedVisible,
  multiple = false,
  onBlur,
  onChange,
  onInputChange,
  onKeyUp,
  onOpenChange,
  options,
  placeholder,
  selectionLimit,
  triggerInputRef,
  value,
  ...restProps
}: AutocompleteComponentProps<T>) => {
  // This is a Jedi Type Trick to make the dependent types work.
  const props = {
    onChange,
    value,
    multiple,
  } as AutocompleteComponentProps<T>;

  const [t] = useTranslation();
  const listboxId = useId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localInputValue, setLocalInputValue] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const previousValue = usePrevious(value);
  const callbacksRef = useRef({ onInputChange, onOpenChange });

  const regularOptions = useMemo(() => options.filter((option): option is AutocompleteRegularOption<T> => !isCustomOption(option)), [options]);
  const areValuesEqual = useCallback((valueA: T, valueB: T) => compareOptionsValues(valueA, valueB, customValueComparer), [customValueComparer]);
  const optionForValue = useMemo(() => {
    if (value === null || Array.isArray(value)) {
      return null;
    }
    return regularOptions.find((option) => areValuesEqual(value, option.value)) ?? null;
  }, [value, regularOptions, areValuesEqual]);

  useEffect(() => {
    if (isOpen !== undefined) {
      setIsDropdownOpen(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    callbacksRef.current.onOpenChange = onOpenChange;
  }, [onOpenChange]);
  useEffect(() => {
    callbacksRef.current.onInputChange = onInputChange;
  }, [onInputChange]);

  useEffect(() => {
    callbacksRef.current?.onOpenChange?.(isDropdownOpen);
  }, [isDropdownOpen]);

  useEffect(() => {
    callbacksRef.current?.onInputChange?.(localInputValue);
  }, [localInputValue]);

  useEffect(() => {
    if (isDisabled && multiple) {
      setLocalInputValue('');
    }
  }, [isDisabled, multiple]);

  useEffect(() => {
    if (value === previousValue || multiple || Array.isArray(value)) {
      return;
    }

    if (value === null) {
      setLocalInputValue('');
      return;
    }

    if (optionForValue) {
      setLocalInputValue(optionForValue.name);
    }
  }, [value, multiple, previousValue, optionForValue]);

  const isOptionSelected = (option: AutocompleteRegularOption<T>): boolean => {
    if (value === null) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.some(selectedValue => areValuesEqual(option.value, selectedValue));
    }

    return areValuesEqual(option.value, value);
  };

  const toggleDropdown = () => setIsDropdownOpen(isOpen => {
    if (isDisabled) {
      return isOpen;
    }
    onOpenChange?.(!isOpen);
    return !isOpen;
  });

  const getOptionMatchesValue = useCallback((option: AutocompleteRegularOption<T>, includePartial?: boolean) => {
    const normalizedInputValue = localInputValue.toLowerCase();
    if (includePartial) {
      return option.name.toLowerCase().includes(normalizedInputValue) ||
        option.aliases?.some(value => value.toLowerCase().includes(normalizedInputValue));
    }
    return option.name.toLowerCase() === normalizedInputValue
      || option.aliases?.some(alias => alias.toLowerCase() === normalizedInputValue);
  }, [localInputValue]);

  const filteredOptions = useMemo(() => {
    const isSelectedValueSameAsInput = !multiple && optionForValue !== null && getOptionMatchesValue(optionForValue);
    if (isSelectedValueSameAsInput) {
      return options;
    }

    return options.filter((option) => {
      if (isCustomOption(option)) {
        return true;
      }

      return getOptionMatchesValue(option, true);
    });
  }, [multiple, optionForValue, getOptionMatchesValue, options]);

  const handleMenuClose = () => {
    setIsDropdownOpen(false);

    if (localInputValue && filteredOptions.length === 1) {
      const remnantOption = filteredOptions[0];
      const isInputMatch = remnantOption && !isCustomOption(remnantOption) && getOptionMatchesValue(remnantOption);
      if (isInputMatch) {
        if (!props.multiple) {
          props.onChange(remnantOption.value);
          setLocalInputValue(remnantOption.name);
        }
        else {
          props.onChange([...props.value, remnantOption.value]);
          setLocalInputValue('');
        }
      }

      return;
    }

    if (!props.multiple && optionForValue) {
      setLocalInputValue(optionForValue.name);
    }
  };

  const handleItemClick = (option: AutocompleteRegularOption<T>) => {
    if (!props.multiple) {
      props.onChange(option.value);
      setLocalInputValue(option.name);
      setIsDropdownOpen(false);
      return;
    }

    let newValue: T[] = [];

    if (isOptionSelected(option)) {
      newValue = props.value.filter(selectedValue => !areValuesEqual(option.value, selectedValue));
    }
    else {
      newValue = [...props.value, option.value];
    }

    props.onChange(newValue);
    setLocalInputValue('');
  };

  const handleOnClear = () => {
    setLocalInputValue('');

    if (!props.multiple) {
      props.onChange(null);
    }
    else {
      props.onChange([]);
    }
  };

  const onValueRemove = (valueToRemove: T) => {
    if (!props.multiple) {
      return;
    }
    const filtered = props.value.filter(selectedValue => !areValuesEqual(valueToRemove, selectedValue));
    props.onChange(filtered);
    return;
  };

  const renderTags = () => {
    if (props.multiple) {
      const selectedInputSize = getSelectedItemSize(inputSize);
      const optionsForValue = regularOptions.filter(option => props.value.some((selectedValue) => areValuesEqual(
        option.value,
        selectedValue)));
      const valueVisiblePortion = optionsForValue.slice(0, maxSelectedVisible ?? optionsForValue.length);
      return (
        <>
          {valueVisiblePortion.map((option, index) => (
            <SelectedItemComponent
              css={selectedItemStyle}
              key={index}
              size={selectedInputSize}
              onRemove={isDisabled ? undefined : (e) => {
                e.stopPropagation();
                onValueRemove(option.value);
              }}
              isDisabled={isLoading}
            >
              <span css={selectedItemTextStyle}>{option.name}</span>
            </SelectedItemComponent>
          ))}
          {props.value.length > valueVisiblePortion.length && (
            <SelectedItemComponent
              css={selectedItemStyle}
              size={selectedInputSize}
            >
              +{props.value.length - valueVisiblePortion.length}
            </SelectedItemComponent>
          )}
        </>
      );
    }
    return undefined;
  };

  const selectedCount = isArray(value) ? value.length : 0;
  const isLimitReached = notNullsy(selectionLimit) && selectedCount >= selectionLimit;

  const isOptionDisabled = useCallback((optionValue: T) => {
    if (!props.multiple || !isLimitReached) {
      return false;
    }
    // Allow deselecting already selected items
    const isSelected = props.value.some(v => areValuesEqual(v, optionValue));
    return !isSelected;

  }, [props.multiple, props.value, isLimitReached, areValuesEqual]);

  const defaultLottieColors = useLottieAnimationDefaultColors();

  const defaultLoaderConfig: LottieAnimationProps = useMemo(() => loaderConfig ?? ({
    ...standardLoaderAnimationSettings,
    size: 45,
    colors: {
      fill: defaultLottieColors.DrippingSpinner?.fill,
    },
  }), [loaderConfig, defaultLottieColors]);

  const hasValue = props.multiple ? props.value.length > 0 : !!localInputValue;

  return (
    <DropdownComponent
      {...restProps}
      inPortal={inPortal}
      isOpen={isDropdownOpen && !isDisabled}
      onClose={handleMenuClose}
      css={dropdownContainerStyle}
      closeOnInsideClick={false}
      dropdownPlacement={DropdownPlacement.BottomStart}
      triggerComponent={(
        <AutocompleteTrigger
          ref={triggerRef}
          inputSize={inputSize}
          inputStyle={inputStyle}
          inputRef={triggerInputRef}
          isOpen={isDropdownOpen}
          value={localInputValue}
          placeholder={placeholder}
          onChange={isDisabled ? undefined : setLocalInputValue}
          onClick={() => {
            setIsDropdownOpen(true);
          }}
          onChevronClick={hideChevron ? undefined : toggleDropdown}
          isDisabled={isDisabled}
          hasValue={hasValue}
          onClear={isClearable && !isDisabled ? handleOnClear : undefined}
          icon={icon}
          iconStyle={iconStyle}
          renderTags={renderTags}
          onBlur={isDisabled ? undefined : onBlur}
          onKeyUp={isDisabled ? undefined : onKeyUp}
          listboxId={listboxId}
        />
      )}
    >
      {isLoading && (
        <OverlayWrapperComponent minHeight={100}>
          <OverlayLottieAnimationComponent
            {...defaultLoaderConfig}
            autoplay
            loop
          />
        </OverlayWrapperComponent>
      )}
      {!isLoading && (
        <div
          role="listbox"
          id={listboxId}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              css={dropdownItemsContainerStyle}
            >
              {isCustomOption(option) ? (
                <>{option.render(inputSize, itemSize)} </>
              ) : (
                <AutocompleteItem
                  name={option.name}
                  onClick={isDisabled || isOptionDisabled(option.value) ? undefined : () => handleItemClick(option)}
                  isDisabled={isDisabled || isOptionDisabled(option.value)}
                  isSelected={checkSelected && isOptionSelected(option)}
                  size={inputSize}
                  subSize={itemSize}
                  prefix={option.prefix}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {!hideNoResultsMessage && !isLoading && filteredOptions.length === 0 && (
        <div css={dropdownItemsContainerStyle}>
          <div css={autocompleteNoResultsStyle({ inputSize, itemSize })}>
            {t('No results')}
          </div>
        </div>
      )}
    </DropdownComponent>
  );
};

const getSelectedItemSize = (inputSize: InputSize) => {
  switch (inputSize) {
    case InputSize.Xs: {
      return Size.XSmall;
    }
    case InputSize.Small:
    case InputSize.Medium: {
      return Size.Small;
    }
    case InputSize.Large:
    default: {
      return Size.Standard;
    }
  }
};
